import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import DialogBaseCreate from "./dialogBaseCreate";
import RuntimeGamesPanel from "./runtimeGamesPanel";
import SelectorPe3dGames from "./selectorPe3dGames";
import { adminScreenFrame, DANGER, MUTED } from "./settingsPalette";
import { findRuntimeBases, renameBase, deleteBase, deleteAllVersionsOfBase } from "../../modules/adminApi";
import { listBaseGames, setBaseGames } from "../../modules/pe3dApi";

/**
 * Runtime bases and games: playEngine resources created from the dashboard
 * (legacy bases still come from database/base/ in git).
 *
 * Two ways in. A superAdmin reaches it from Companies & Games,
 * which passes the toggle back to Companies as `headerAction` and reloads its
 * grant picker through `onChanged`. A user a superAdmin gave manageRuntimeBases
 * gets it as a page of its own, where it can only create: renaming a base,
 * deleting a game or base, and emptying a base of versions, stay
 * superAdmin-only (`canDelete`).
 *
 * A pe3d base also shows which PlayEngine3D games it offers, in a checklist of
 * every imported game: whoever holds "Grant pe3d games to bases" changes them
 * here (`canGrantGames`; the same list as the dialog on the base's own variant
 * list). A user who holds only that grant gets this page for the pe3d bases it
 * reaches, with nothing to create (`canCreate` false).
 *
 * @param props.createSnackbar
 * @param props.canDelete
 * @param [props.canCreate]      defaults to true
 * @param [props.canGrantGames]
 * @param [props.headerAction]
 * @param [props.onChanged]
 */

const useStyles = makeStyles(() => ({
  ...adminScreenFrame,

  // Same warning rim as the Internal tools block in the superAdmin panel.
  dangerBlock: {
    marginTop: "0.5rem",
    padding: "0.75rem 1rem",
    border: `1px solid ${DANGER}`,
    borderRadius: "4px",
  },
  dangerTitle: { fontSize: "0.72rem", letterSpacing: 0.5, textTransform: "uppercase", color: DANGER },
  dangerActions: { display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "0.5rem" },
  dangerButton: { color: DANGER, borderColor: DANGER },

  // The detail title with its Rename button, or the rename field in its place.
  // The row takes detailTitle's padding so the title sits where it always did.
  titleRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", padding: "0.75rem 1.25rem 0 1.25rem", flexShrink: 0 },
  titleInRow: { padding: 0, minWidth: 0, overflowWrap: "anywhere" },
  renameField: { flex: 1, minWidth: "200px", maxWidth: "380px" },

  // Rows: the text takes the room, the kind chip keeps its size.
  rowText: { flex: 1, minWidth: 0 },
  rowChip: { flexShrink: 0, marginLeft: "8px" },
  loadingRow: { display: "flex", alignItems: "center", gap: "8px", fontSize: "0.75rem", color: MUTED },
}));

export default function RuntimeBasesPanel(props) {
  const classes = useStyles();
  const { canDelete, canGrantGames } = props;
  const canCreate = props.canCreate !== false;

  const [bases, setBases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedBaseID, setSelectedBaseID] = useState(null);
  const [isBaseDialogOpen, setIsBaseDialogOpen] = useState(false);
  const [view, setView] = useState("bases");
  // The selected pe3d base's games: { baseID, games, onBase, error }, null while loading.
  const [baseGames, setBaseGamesState] = useState(null);
  const [gameDraft, setGameDraft] = useState([]);
  // Typed-title confirmation for the two destructive actions; which one is
  // pending decides what the confirm button does.
  const [pendingAction, setPendingAction] = useState(null); // "deleteVersions" | "deleteBase" | null
  const [confirmTitle, setConfirmTitle] = useState("");
  const [blockingVersionCount, setBlockingVersionCount] = useState(null);
  // The new title while renaming, null otherwise.
  const [renameDraft, setRenameDraft] = useState(null);

  const selectedBase = bases.find((base) => base._id === selectedBaseID) || null;

  const reload = async () => {
    const result = await findRuntimeBases();
    if (result.ok) {
      setBases(result.data || []);
    } else {
      props.createSnackbar("Could not load the runtime bases.", "error", false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    reload();
  }, []);

  const isPe3dSelected = Boolean(selectedBase && selectedBase.kind === "pe3d");
  useEffect(() => {
    if (!isPe3dSelected || !canGrantGames) return undefined;
    let isCurrent = true;
    setBaseGamesState(null);
    listBaseGames(selectedBaseID).then((result) => {
      if (!isCurrent) return;
      const onBase = result.ok ? result.data.onBase || [] : [];
      setBaseGamesState({
        baseID: selectedBaseID,
        games: result.ok ? result.data.games || [] : [],
        onBase,
        error: result.ok ? null : typeof result.error === "string" ? result.error : `HTTP ${result.status}`,
      });
      setGameDraft(onBase);
    });
    return () => {
      isCurrent = false;
    };
  }, [selectedBaseID, isPe3dSelected, canGrantGames]);

  const gamesOfSelected = baseGames && baseGames.baseID === selectedBaseID ? baseGames : null;
  const isGameListChanged = Boolean(gamesOfSelected) && [...gameDraft].sort().join(",") !== [...gamesOfSelected.onBase].sort().join(",");

  const saveGames = async () => {
    setIsSaving(true);
    const result = await setBaseGames(selectedBaseID, gameDraft);
    setIsSaving(false);
    if (!result.ok) {
      props.createSnackbar(`Could not save the games: ${typeof result.error === "string" ? result.error : result.status}`, "error", false);
      return;
    }
    const onBase = result.data.onBase || [];
    setBaseGamesState((current) => (current && current.baseID === selectedBaseID ? { ...current, onBase } : current));
    setGameDraft(onBase);
    props.createSnackbar(`${selectedBase.title}: ${onBase.length} game${onBase.length === 1 ? "" : "s"} on it.`, "success", false);
    await reload();
  };

  const afterChange = async () => {
    await reload();
    if (props.onChanged) props.onChanged();
    if (props.onRuntimeChanged) props.onRuntimeChanged();
  };

  const selectBase = (base) => {
    setSelectedBaseID(base._id);
    setPendingAction(null);
    setConfirmTitle("");
    setBlockingVersionCount(null);
    setRenameDraft(null);
  };

  const onBaseCreated = async () => {
    setIsBaseDialogOpen(false);
    await afterChange();
  };

  const renamedTitle = renameDraft === null ? "" : renameDraft.trim();
  const canSaveRename = Boolean(selectedBase) && renamedTitle.length > 0 && renamedTitle.length <= 120 && renamedTitle !== selectedBase.title;

  const runRename = async () => {
    if (!canSaveRename) return;
    setIsSaving(true);
    const result = await renameBase(selectedBase._id, renamedTitle);
    setIsSaving(false);
    if (!result.ok) {
      props.createSnackbar(`Could not rename the base: ${result.data || result.status}`, "error", false);
      return;
    }
    setRenameDraft(null);
    props.createSnackbar(`${selectedBase.title} renamed to ${result.data.title}.`, "success", false);
    await afterChange();
  };

  const runDeleteVersions = async () => {
    if (!selectedBase) return;
    setIsSaving(true);
    const result = await deleteAllVersionsOfBase(selectedBase._id);
    setIsSaving(false);
    setPendingAction(null);
    setConfirmTitle("");
    if (!result.ok) {
      props.createSnackbar(`Could not delete the versions: ${result.data || result.status}`, "error", false);
      return;
    }
    setBlockingVersionCount(0);
    props.createSnackbar(`${selectedBase.title}: ${result.data.deletedVersions} versions deleted.`, "success", false);
  };

  const runDeleteBase = async () => {
    if (!selectedBase) return;
    setIsSaving(true);
    const result = await deleteBase(selectedBase._id);
    setIsSaving(false);
    setPendingAction(null);
    setConfirmTitle("");
    if (result.status === 409 && result.data && result.data.error === "BASE_HAS_VERSIONS") {
      setBlockingVersionCount(result.data.versionCount);
      props.createSnackbar(
        `${selectedBase.title} still holds ${result.data.versionCount} versions. Delete them first.`,
        "warning",
        false
      );
      return;
    }
    if (!result.ok) {
      props.createSnackbar(`Could not delete the base: ${result.data || result.status}`, "error", false);
      return;
    }
    props.createSnackbar(`${selectedBase.title} deleted.`, "success", false);
    setSelectedBaseID(null);
    await afterChange();
  };

  if (view === "games") {
    return <RuntimeGamesPanel canDelete={canDelete} createSnackbar={props.createSnackbar} headerAction={props.headerAction} onShowBases={() => setView("bases")} />;
  }

  if (isLoading) {
    return (
      <div className={classes.root}>
        <div className={classes.centred}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>{canCreate ? "Runtime bases" : "PE3D bases"}</Typography>
        <div className={classes.headerSpacer} />
        {props.headerAction}
        {canCreate && (
          <>
            <Button variant="outlined" onClick={props.onShowGames || (() => setView("games"))}>Runtime games</Button>
            <Button variant="contained" color="primary" onClick={() => setIsBaseDialogOpen(true)}>
              + New base
            </Button>
          </>
        )}
      </div>

      <DialogBaseCreate
        isOpen={isBaseDialogOpen}
        onClose={() => setIsBaseDialogOpen(false)}
        onCreated={onBaseCreated}
        createSnackbar={props.createSnackbar}
      />

      <div className={classes.body}>
        <div className={classes.listColumn}>
          <div className={classes.listHeader}>
            <div className={`${classes.listLabel} ${classes.listLabelSpaced}`}>{canCreate ? "Runtime bases" : "PE3D bases"} ({bases.length})</div>
          </div>
          <div className={classes.listScroller}>
            {bases.length === 0 && (
              <div className={classes.detailEmpty}>
                {canCreate ? "No runtime bases yet. Legacy bases come from git." : "Your company holds no PE3D base."}
              </div>
            )}
            {bases.map((base) => (
              <div
                key={base._id}
                className={`${classes.listRow} ${base._id === selectedBaseID ? classes.listRowSelected : ""}`}
                onClick={() => selectBase(base)}
              >
                <div className={classes.rowText}>
                  <div className={classes.rowTitle}>{base.title}</div>
                  <div className={classes.rowMeta}>{base.gameTitle}</div>
                </div>
                {/* Right-aligned, so the kinds line up down the list however
                    long the titles wrap. */}
                <span className={`${classes.chip} ${classes.chipOutline} ${classes.rowChip}`}>
                  {base.kind === "snapshotEditor" ? base.snapshotEditorFlow || "editor" : base.kind === "pe3d" ? "pe3d" : "versioned"}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.detailColumn}>
          {!selectedBase && <div className={classes.detailEmpty}>Select a base.</div>}
          {selectedBase && (
            <>
              <div className={classes.titleRow}>
                {renameDraft === null && (
                  <>
                    <Typography className={`${classes.detailTitle} ${classes.titleInRow}`}>{selectedBase.title}</Typography>
                    {canDelete && (
                      <Button variant="outlined" size="small" disabled={isSaving} onClick={() => setRenameDraft(selectedBase.title)}>
                        Rename
                      </Button>
                    )}
                  </>
                )}
                {renameDraft !== null && (
                  <>
                    <TextField
                      className={classes.renameField}
                      size="small"
                      autoFocus
                      value={renameDraft}
                      inputProps={{ maxLength: 120 }}
                      onChange={(event) => setRenameDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") runRename();
                        if (event.key === "Escape") setRenameDraft(null);
                      }}
                    />
                    <Button variant="contained" color="primary" size="small" disabled={isSaving || !canSaveRename} onClick={runRename}>
                      Save
                    </Button>
                    <Button variant="outlined" size="small" disabled={isSaving} onClick={() => setRenameDraft(null)}>
                      Cancel
                    </Button>
                  </>
                )}
              </div>
              <div className={classes.detailRule} />
              <div className={classes.tabBody}>
                <Typography className={classes.hint}>
                  {selectedBase.gameTitle} · {selectedBase.kind}
                  {selectedBase.snapshotEditorFlow ? ` · flow ${selectedBase.snapshotEditorFlow}` : ""}
                  {" · id "}
                  {selectedBase._id}
                </Typography>
                <Typography className={classes.hint}>
                  {canDelete
                    ? "Grant it to companies from the Companies view. Nothing here needs a deploy."
                    : "A base you create is granted to your own company; a superAdmin grants it to others. Nothing here needs a deploy."}
                </Typography>

                {renameDraft !== null && (
                  <Typography className={classes.hint}>
                    Only the name changes; versions, grants and export links follow the base by its id. Later exports whose
                    build profile uses the base title get the new name.
                  </Typography>
                )}

                {selectedBase.kind === "pe3d" && (
                  <div className={classes.block}>
                    <Typography className={classes.subTitle}>Games on this base</Typography>
                    {!canGrantGames && (
                      <Typography className={classes.hint}>
                        {(selectedBase.pe3dGames || []).length
                          ? `New variants can be started from: ${selectedBase.pe3dGames.map((name) => name.split("/").pop()).join(", ")}.`
                          : "None yet, so no variant can be started here."}{" "}
                        They are chosen by whoever holds &ldquo;Grant pe3d games to bases&rdquo;.
                      </Typography>
                    )}
                    {canGrantGames && (
                      <>
                        <Typography className={classes.hint}>
                          A new variant on this base can only be started from the games ticked here. Unticking one keeps the variants
                          already made of it: they still open, export and duplicate.
                        </Typography>
                        {!gamesOfSelected && (
                          <div className={classes.loadingRow}>
                            <CircularProgress size={14} />
                            Loading the games…
                          </div>
                        )}
                        {gamesOfSelected && gamesOfSelected.error && (
                          <Typography className={classes.hint}>
                            {gamesOfSelected.error === "USER_IS_NOT_ALLOWED_TO_WORK_ON_BASE"
                              ? "Your company does not hold this base, so its games are not yours to choose."
                              : `The games could not be loaded (${gamesOfSelected.error}).`}
                          </Typography>
                        )}
                        {gamesOfSelected && !gamesOfSelected.error && (
                          <SelectorPe3dGames
                            games={gamesOfSelected.games}
                            selected={gameDraft}
                            onChange={setGameDraft}
                            emptyText="No PlayEngine3D game has been imported yet. A superAdmin imports them under PE3D games."
                          />
                        )}
                      </>
                    )}
                  </div>
                )}

                {canDelete && (
                  <div className={classes.dangerBlock}>
                    <Typography className={classes.dangerTitle}>Delete</Typography>
                    <Typography className={classes.hint}>
                    A base is only deleted once it holds no versions; the grants that name it are cleaned up
                    with it.{" "}
                      {selectedBase.kind === "pe3d"
                        ? "A PlayEngine3D base's variants are deleted from its editor."
                        : "Deleting all versions removes them for every company and cannot be undone."}
                      {blockingVersionCount > 0 ? ` This base holds ${blockingVersionCount} versions.` : ""}
                    </Typography>
                    {pendingAction === null && (
                      <div className={classes.dangerActions}>
                        {/* versionPlayEngine/deleteAllOfBase empties versionPlayEngine only. */}
                        {selectedBase.kind !== "pe3d" && (
                          <Button
                            variant="outlined"
                            className={classes.dangerButton}
                            disabled={isSaving}
                            onClick={() => setPendingAction("deleteVersions")}
                          >
                          Delete all versions
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          className={classes.dangerButton}
                          disabled={isSaving}
                          onClick={() => setPendingAction("deleteBase")}
                        >
                        Delete base
                        </Button>
                      </div>
                    )}
                    {pendingAction !== null && (
                      <>
                        <Typography className={classes.hint} style={{ marginTop: "0.5rem" }}>
                        Type the base title to confirm{" "}
                          {pendingAction === "deleteVersions" ? "deleting every version" : "deleting the base"}.
                        </Typography>
                        <TextField
                          className={classes.field}
                          size="small"
                          placeholder={selectedBase.title}
                          value={confirmTitle}
                          onChange={(event) => setConfirmTitle(event.target.value)}
                        />
                        <div className={classes.dangerActions}>
                          <Button
                            variant="contained"
                            className={classes.dangerButton}
                            disabled={isSaving || confirmTitle.trim() !== selectedBase.title}
                            onClick={pendingAction === "deleteVersions" ? runDeleteVersions : runDeleteBase}
                          >
                            {pendingAction === "deleteVersions" ? "Delete all versions" : "Delete base"}
                          </Button>
                          <Button
                            variant="outlined"
                            disabled={isSaving}
                            onClick={() => {
                              setPendingAction(null);
                              setConfirmTitle("");
                            }}
                          >
                          Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              {isPe3dSelected && canGrantGames && gamesOfSelected && !gamesOfSelected.error && (
                <div className={classes.footer}>
                  <Button variant="contained" color="primary" disabled={isSaving || !isGameListChanged} onClick={saveGames}>
                    Save games
                  </Button>
                  <Button variant="outlined" disabled={isSaving || !isGameListChanged} onClick={() => setGameDraft(gamesOfSelected.onBase)}>
                    Reset
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
